import React, { useState, useEffect } from 'react'
import Footer from '../components/footer'
import Navbar from '../components/navbar'
import { featureFlagsApi, FeatureFlag } from '../services/feature-flags-api'

interface FeatureFlagFormProps {
  flag: FeatureFlag | null
  onSave: (flag: FeatureFlag) => void
  onCancel: () => void
  isSaving: boolean
}

const FeatureFlagForm: React.FC<FeatureFlagFormProps> = ({ flag, onSave, onCancel, isSaving }) => {
  const [key, setKey] = useState(flag?.key || '')
  const [name, setName] = useState(flag?.name || '')
  const [description, setDescription] = useState(flag?.description || '')
  const [enabled, setEnabled] = useState(flag?.enabled ?? false)
  const [targetedEmailsText, setTargetedEmailsText] = useState(
    flag?.targetedEmails?.join('\n') || ''
  )

  const handleSubmit = () => {
    const targetedEmails = targetedEmailsText
      .split('\n')
      .map(e => e.trim())
      .filter(e => e.length > 0)

    const newFlag: FeatureFlag = {
      id: flag?.id || '',
      key: key.trim(),
      name: name.trim(),
      description: description.trim(),
      enabled,
      targetedEmails,
      createdAt: flag?.createdAt,
      updatedAt: flag?.updatedAt,
    }

    onSave(newFlag)
  }

  const isValid = key.trim() !== '' && name.trim() !== ''

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {flag ? 'Edit Feature Flag' : 'Create New Feature Flag'}
      </h3>
      <div className="space-y-4">
        <div>
          <label htmlFor="flagKey" className="block text-sm font-medium text-gray-700 mb-1">
            Key
          </label>
          <input
            id="flagKey"
            type="text"
            placeholder="e.g. recurring_donations"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={!!flag}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
              flag ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
            }`}
          />
          {flag && (
            <p className="text-xs text-gray-500 mt-1">Key cannot be changed after creation</p>
          )}
        </div>

        <div>
          <label htmlFor="flagName" className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            id="flagName"
            type="text"
            placeholder="e.g. Recurring Donations"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="flagDescription" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="flagDescription"
            placeholder="Describe what this feature flag controls..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Enabled
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Toggle the feature flag on or off globally
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEnabled(!enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              enabled ? 'bg-teal-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div>
          <label htmlFor="targetedEmails" className="block text-sm font-medium text-gray-700 mb-1">
            Targeted Emails (one per line)
          </label>
          <textarea
            id="targetedEmails"
            placeholder="user1@example.com&#10;user2@example.com"
            value={targetedEmailsText}
            onChange={(e) => setTargetedEmailsText(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            When the flag is off globally, these users will still have access
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || isSaving}
            className={`px-4 py-2 text-white rounded-lg transition-colors ${
              isValid && !isSaving
                ? 'bg-teal-600 hover:bg-teal-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface FeatureFlagCardProps {
  flag: FeatureFlag
  onToggle: (id: string, enabled: boolean) => void
  onEdit: (flag: FeatureFlag) => void
  onDelete: (id: string) => void
  isToggling: boolean
}

const FeatureFlagCard: React.FC<FeatureFlagCardProps> = ({ flag, onToggle, onEdit, onDelete, isToggling }) => {
  const [showEmails, setShowEmails] = useState(false)

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-gray-800">{flag.name}</h3>
            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded text-gray-600">
              {flag.key}
            </span>
          </div>
          {flag.description && (
            <p className="text-sm text-gray-600 mt-1">{flag.description}</p>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={() => onToggle(flag.id, !flag.enabled)}
              disabled={isToggling}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                flag.enabled ? 'bg-teal-600' : 'bg-gray-300'
              } ${isToggling ? 'opacity-50 cursor-wait' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  flag.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-xs font-medium ${flag.enabled ? 'text-green-600' : 'text-gray-500'}`}>
              {flag.enabled ? 'Global: ON' : 'Global: OFF'}
            </span>
          </div>
        </div>
      </div>

      {/* Targeted Emails & Badges */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {!flag.enabled && flag.targetedEmails.length > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
            Targeted: {flag.targetedEmails.length} user{flag.targetedEmails.length !== 1 ? 's' : ''}
          </span>
        )}
        {flag.targetedEmails.length > 0 && (
          <button
            type="button"
            onClick={() => setShowEmails(!showEmails)}
            className="text-xs text-teal-600 hover:text-teal-700 hover:underline"
          >
            {showEmails ? 'Hide' : 'Show'} {flag.targetedEmails.length} targeted email{flag.targetedEmails.length !== 1 ? 's' : ''}
          </button>
        )}
      </div>

      {showEmails && flag.targetedEmails.length > 0 && (
        <div className="mt-2 bg-gray-50 rounded-lg p-3">
          <div className="space-y-1">
            {flag.targetedEmails.map((email, idx) => (
              <div key={idx} className="text-sm text-gray-700 font-mono">{email}</div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
        <button
          type="button"
          onClick={() => onEdit(flag)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(flag.id)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Delete
        </button>
      </div>
    </div>
  )
}

const FeatureFlagsPage: React.FC = () => {
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchFlags = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await featureFlagsApi.listFeatureFlags()
      setFlags(data)
    } catch (err) {
      console.error('Failed to fetch feature flags:', err)
      setError('Failed to load feature flags. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchFlags()
  }, [])

  const handleSave = async (flag: FeatureFlag) => {
    try {
      setIsSaving(true)
      setError(null)

      if (editingFlag) {
        const updated = await featureFlagsApi.updateFeatureFlag(editingFlag.id, flag)
        setFlags(prev => prev.map(f => f.id === updated.id ? updated : f))
      } else {
        const created = await featureFlagsApi.createFeatureFlag(flag)
        setFlags(prev => [...prev, created])
      }

      setEditingFlag(null)
      setIsAddingNew(false)
    } catch (err) {
      console.error('Failed to save feature flag:', err)
      setError('Failed to save feature flag. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggle = async (id: string, enabled: boolean) => {
    try {
      setTogglingId(id)
      setError(null)
      const updated = await featureFlagsApi.updateFeatureFlag(id, { enabled })
      setFlags(prev => prev.map(f => f.id === updated.id ? updated : f))
    } catch (err) {
      console.error('Failed to toggle feature flag:', err)
      setError('Failed to update feature flag. Please try again.')
    } finally {
      setTogglingId(null)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this feature flag? This cannot be undone.')) {
      return
    }

    try {
      setError(null)
      await featureFlagsApi.deleteFeatureFlag(id)
      setFlags(prev => prev.filter(f => f.id !== id))
    } catch (err) {
      console.error('Failed to delete feature flag:', err)
      setError('Failed to delete feature flag. Please try again.')
    }
  }

  const handleEdit = (flag: FeatureFlag) => {
    setEditingFlag(flag)
    setIsAddingNew(false)
  }

  const handleCancel = () => {
    setEditingFlag(null)
    setIsAddingNew(false)
  }

  const showForm = isAddingNew || editingFlag !== null

  return (
    <div className="flex flex-col h-screen">
      <Navbar />
      <div className="md:container mx-auto flex-1 py-8 px-4">
        <div className="w-full max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Feature Flags</h1>
              <p className="text-gray-600 text-sm mt-1">
                Manage feature flags to control feature availability
              </p>
            </div>
            {!showForm && !isLoading && (
              <button
                onClick={() => setIsAddingNew(true)}
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Flag
              </button>
            )}
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <svg className="w-8 h-8 animate-spin text-teal-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="ml-3 text-gray-600">Loading feature flags...</span>
            </div>
          )}

          {!isLoading && (
            <>
              {/* Create/Edit Form */}
              {showForm && (
                <div className="mb-6">
                  <FeatureFlagForm
                    flag={editingFlag}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    isSaving={isSaving}
                  />
                </div>
              )}

              {/* Feature Flag Cards */}
              {!showForm && (
                <div className="space-y-4">
                  {flags.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p className="text-lg">No feature flags yet</p>
                      <p className="text-sm mt-1">Create a new flag to get started</p>
                    </div>
                  ) : (
                    flags.map(flag => (
                      <FeatureFlagCard
                        key={flag.id}
                        flag={flag}
                        onToggle={handleToggle}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        isToggling={togglingId === flag.id}
                      />
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default FeatureFlagsPage
